import { createContext, type MutableRefObject, useContext } from "react";
import type { NavigateFunction } from "react-router";

export type SheetModeCallback = (remoteId: string) => void;

export type SetSheetMode = (callback: SheetModeCallback | null) => void;

export const SheetModeContext = createContext<SetSheetMode | null>(null);

export function useSheetMode(): SetSheetMode {
    const ctx = useContext(SheetModeContext);
    if (!ctx) {
        throw new Error(
            "useSheetMode must be used within a RuntimeProvider tree"
        );
    }
    return ctx;
}

// Remote thread id of the currently open sheet, or null when no sheet is open.
// Mirrors the registry pattern of chat-sheet.thread-ownership.ts.
let activeSheetThreadId: string | null = null;

export function getActiveSheetThreadId(): string | null {
    return activeSheetThreadId;
}

export function setActiveSheetThreadId(id: string | null): void {
    activeSheetThreadId = id;
}

interface IsOffChatInput {
    search: string;
    pathname: string;
    remoteId: string;
    activeSheetThreadId: string | null;
}

export function computeIsOffChat({
    search,
    pathname,
    remoteId,
    activeSheetThreadId: sheetThreadId,
}: IsOffChatInput): boolean {
    // Suppress when this exact thread is currently open in a sheet.
    if (sheetThreadId === remoteId) {
        return false;
    }
    const urlThreadId = new URLSearchParams(search).get("threadId");
    if (urlThreadId !== null) {
        return urlThreadId !== remoteId;
    }
    return !pathname.endsWith(`/chat/${remoteId}`);
}

interface WrapInitializeDeps<TResult extends { remoteId: string }> {
    baseInitialize: (threadId: string) => Promise<TResult>;
    navigateRef: MutableRefObject<NavigateFunction>;
    remoteIdRef: MutableRefObject<string | undefined>;
    sheetModeRef: MutableRefObject<SheetModeCallback | null>;
}

export function wrapInitializeWithSheetMode<
    TResult extends { remoteId: string },
>({
    baseInitialize,
    navigateRef,
    remoteIdRef,
    sheetModeRef,
}: WrapInitializeDeps<TResult>): (threadId: string) => Promise<TResult> {
    return async (threadId: string) => {
        // Snapshot pre-await: if the sheet closes mid-flight the navigate
        // branch would otherwise teleport the user to /organizations/chat/:id.
        const sheetCallbackAtStart = sheetModeRef.current;
        const result = await baseInitialize(threadId);
        remoteIdRef.current = result.remoteId;
        if (sheetCallbackAtStart) {
            const callback = sheetModeRef.current ?? sheetCallbackAtStart;
            callback(result.remoteId);
        } else {
            navigateRef.current(`/organizations/chat/${result.remoteId}`, {
                replace: true,
            });
        }
        return result;
    };
}
